import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import FilterSidebar, { type CategoryFilters } from "@/pages/category/components/FilterSidebar";
import WorkListRow from "@/components/feature/WorkListRow";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  availableYears,
  filterWorks,
  getCategory,
  sortWorks,
  type SortKey,
} from "@/utils/videoUtils";

const PAGE_SIZE = 6;

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "latest", label: "综合排序" },
  { value: "popular", label: "按热度" },
  { value: "rating", label: "按评分" },
  { value: "year", label: "按年份" },
];

const DEFAULT_FILTERS: CategoryFilters = { media: "all", year: "all", minRating: "all" };

export default function Category() {
  const { type = "drama" } = useParams();
  const category = getCategory(type);
  const [filters, setFilters] = useState<CategoryFilters>(DEFAULT_FILTERS);
  const [sort, setSort] = useState<SortKey>("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  usePageMeta(
    category ? `日本伦理剧 - ${category.name}影视作品列表` : "日本伦理剧 - 分类影视作品列表",
    category
      ? `${category.name}频道，${category.description}`
      : "日本伦理剧分类影视作品列表，按类型、年份与评分筛选你感兴趣的伦理剧情作品。",
    category ? `${category.name},日本伦理剧,伦理剧情,日本影视` : "日本伦理剧,伦理剧情,影视分类",
  );

  useEffect(() => {
    setFilters(DEFAULT_FILTERS);
    setSort("latest");
    setVisible(PAGE_SIZE);
    setMobileFilterOpen(false);
  }, [type]);

  const years = useMemo(() => availableYears(type), [type]);

  const resultList = useMemo(
    () => sortWorks(filterWorks(type, filters), sort),
    [type, filters, sort],
  );

  const shown = resultList.slice(0, visible);

  const handleChange = (next: Partial<CategoryFilters>) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setVisible(PAGE_SIZE);
  };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setVisible(PAGE_SIZE);
  };

  if (!category) {
    return (
      <div className="w-full px-4 md:px-6 py-24 text-center">
        <i className="ri-emotion-sad-line text-5xl text-foreground-400"></i>
        <h1 className="mt-4 font-heading text-xl font-semibold text-foreground-950">未找到该分类</h1>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-700 text-background-50 font-label text-sm hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full px-4 md:px-6 py-8">
      {/* 面包屑 */}
      <nav aria-label="面包屑" className="text-sm font-label text-foreground-600 mb-4">
        <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
        <i className="ri-arrow-right-s-line mx-1"></i>
        <span>分类</span>
        <i className="ri-arrow-right-s-line mx-1"></i>
        <span className="text-foreground-900">{category.name}</span>
      </nav>

      {/* 标题与说明 */}
      <div className="rounded-lg bg-primary-900 text-background-100 p-6 md:p-8 mb-6">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 flex items-center justify-center rounded-md bg-accent-500 text-accent-950">
            <i className={`${category.icon} text-2xl`}></i>
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-background-50">{category.name}</h1>
            <p className="mt-1 text-sm text-background-200/85 max-w-2xl">{category.description}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* 左侧筛选 */}
        <aside className="w-full lg:w-64 shrink-0">
          <button
            type="button"
            onClick={() => setMobileFilterOpen((v) => !v)}
            className="lg:hidden w-full h-11 mb-3 rounded-md border border-background-300 bg-background-50 text-sm font-label text-foreground-800 flex items-center justify-center gap-2 cursor-pointer"
          >
            <i className="ri-filter-3-line"></i> 筛选条件
          </button>
          <div className={`${mobileFilterOpen ? "block" : "hidden"} lg:block lg:sticky lg:top-20`}>
            <FilterSidebar
              years={years}
              filters={filters}
              onChange={handleChange}
              onReset={handleReset}
            />
          </div>
        </aside>

        {/* 右侧列表 */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm font-label text-foreground-600">
              共 <span className="text-primary-700 font-semibold">{resultList.length}</span> 部作品
            </p>
            <div className="flex items-center gap-2">
              <label htmlFor="sort-select" className="text-sm font-label text-foreground-600 whitespace-nowrap">
                排序：
              </label>
              <select
                id="sort-select"
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value as SortKey);
                  setVisible(PAGE_SIZE);
                }}
                className="h-10 px-3 pr-8 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {resultList.length === 0 ? (
            <div className="rounded-lg border border-background-200/70 bg-background-50 py-20 text-center">
              <i className="ri-empathize-line text-5xl text-foreground-400"></i>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">暂无符合条件的作品</h2>
              <p className="mt-2 text-sm text-foreground-600">试试调整筛选条件，或重置查看全部内容</p>
              <button
                type="button"
                onClick={handleReset}
                className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-accent-950 font-label text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line"></i> 重置筛选
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                {shown.map((work) => (
                  <WorkListRow key={work.id} work={work} />
                ))}
              </div>
              {visible < resultList.length && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 h-11 px-8 rounded-md border border-primary-300 bg-background-50 text-primary-700 font-label text-sm hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    加载更多 <i className="ri-arrow-down-line"></i>
                  </button>
                  <p className="mt-2 text-xs text-foreground-500 font-label">
                    已显示 {shown.length} / {resultList.length}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}